import React from "react";
import styles from "./previewBlog.module.scss";

const PreviewBlog = ({ blog = {}, categoryPreviewLabel = "" }) => {
  const sections = Array.isArray(blog.sections) ? blog.sections : [];
  const hasPreviewContent = Boolean(
    (blog && blog.title) ||
      (blog && blog.imagePreviewDataUrl) ||
      (blog && blog.imageUrl) ||
      sections.length > 0
  );

  const listStyleToCss = (style = "bullet") => {
    if (style === "roman") return "lower-roman";
    if (style === "alpha") return "lower-alpha";
    if (style === "decimal") return "decimal";
    return "disc";
  };

  const buildListTree = (items = []) => {
    const root = { children: [] };
    const stack = [root];

    items.forEach((item, idx) => {
      const level = Math.max(0, Math.min(4, Number(item?.level) || 0));
      while (stack.length > level + 1) {
        stack.pop();
      }
      const parent = stack[stack.length - 1];
      const node = {
        ...item,
        id: item?.id || `list-${idx}`,
        children: [],
      };
      parent.children.push(node);
      stack[level + 1] = node;
      stack.length = level + 2;
    });

    return root.children;
  };

  const renderListNodes = (nodes, style, depth = 0) => {
    if (!nodes || nodes.length === 0) return null;
    const isBullet = style === "bullet";
    const ListTag = isBullet ? "ul" : "ol";
    return (
      <ListTag
        className={styles.list}
        style={{
          listStyleType: listStyleToCss(style),
          paddingLeft: depth === 0 ? "18px" : "20px",
        }}
      >
        {nodes.map((node) => (
          <li key={node.id}>
            {node.text || (
              <span className={styles.cellPlaceholder}>Untitled item</span>
            )}
            {renderListNodes(node.children, style, depth + 1)}
          </li>
        ))}
      </ListTag>
    );
  };

  if (!hasPreviewContent) return null;

  return (
    <div className={styles.preview}>
      <div className={styles.previewHeader}>Preview</div>

      {categoryPreviewLabel && (
        <div className={styles.badge}>{categoryPreviewLabel}</div>
      )}

      {blog.title && <h1 className={styles.title}>{blog.title}</h1>}

      {(blog.imagePreviewDataUrl || blog.imageUrl) && (
        <div className={styles.heroWrap}>
          <img
            className={styles.heroImage}
            src={blog.imagePreviewDataUrl || blog.imageUrl}
            alt={blog.title || "blog image"}
          />
        </div>
      )}

      {blog.miniDescription && (
        <p className={styles.subtitle}>{blog.miniDescription}</p>
      )}

      <div className={styles.article}>
        {sections.map((section) => {
          if (section.type === "heading" && section.value) {
            return (
              <h2 className={styles.h2} key={section.id}>
                {section.value}
              </h2>
            );
          }
          if (section.type === "subHeading" && section.value) {
            return (
              <h3 className={styles.h3} key={section.id}>
                {section.value}
              </h3>
            );
          }
          if (section.type === "paragraph" && section.value) {
            return (
              <p className={styles.p} key={section.id}>
                {section.value}
              </p>
            );
          }
          if (section.type === "image" && (section.imageUrl || section.previewDataUrl)) {
            return (
              <div className={styles.inlineImage} key={section.id}>
                <img
                  src={section.imageUrl || section.previewDataUrl}
                  alt={section.alt || "section"}
                />
              </div>
            );
          }
          if (section.type === "table") {
            return (
              <div className={styles.tablePreview} key={section.id}>
                <table>
                  <tbody>
                    {Array.isArray(section.rows) &&
                      section.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {Array.isArray(row) &&
                            row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {cell || (
                                  <span className={styles.cellPlaceholder}>—</span>
                                )}
                              </td>
                            ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            );
          }
          if (section.type === "list") {
            const normalizedItems = Array.isArray(section.items)
              ? section.items
              : [];
            const tree = buildListTree(normalizedItems);
            return (
              <div className={styles.listPreview} key={section.id}>
                {renderListNodes(tree, section.listStyle || "bullet")}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default PreviewBlog;
